import React, { useState } from 'react';
import { Button } from '../UI/Button';
interface EngagementFormProps {
  onFormSubmit: (formData: any) => void;
}
export const EngagementForm = ({
  onFormSubmit
}: EngagementFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    sex: '',
    phone: '',
    location: '',
    industry: '',
    occupation: '',
    commitment: '',
    mediaConsent: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('https://formspree.io/f/mpwyowlw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        // Call the parent component's callback to move to the next step
        onFormSubmit(formData);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white p-6 rounded-lg border-2 border-gray-300" data-id="element-140">
      <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-141">
        The Networking Salon — How to Engage
      </h2>
      <div className="mb-6" data-id="element-142">
        <p className="text-gray-700 mb-4" data-id="element-143">Welcome to The Networking Salon!</p>
        <p className="text-gray-700 mb-4" data-id="element-144">
          Hey there! We're so excited to have you as part of our community. This
          is a space full of energy, ideas, and collaboration—where young people
          come together to learn, grow, and lift each other up.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-145">
          Here we build intimate, high-trust ecosystems that connect people to
          transformative relationships, opportunities, and visibility.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-146">
          We host curated salon-style gatherings, peer masterminds, local and
          global sessions, opportunity boards, and feedback loops.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-147">
          Members gain visibility, access curated grants, fellowships,
          collaborations, and speaking/media opportunities — each one making
          room for another!
        </p>
        <p className="text-gray-700 mb-4" data-id="element-148">
          To keep this space thriving, we've put together a few simple
          guidelines. They're here to help us all get the most out of this
          experience and make sure everyone feels supported, respected, and
          empowered.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-149">
          By agreeing to these rules, you're not just joining a community—you're
          helping shape it. Your engagement, positivity, and commitment to these
          values make a real difference.
        </p>
        <p className="text-gray-700 mb-6" data-id="element-150">
          Let's build something great together!
        </p>
      </div>
      <div className="mb-8 border-2 border-dashed border-gray-300 p-6 rounded-lg" data-id="element-151">
        <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-152">
          Community Engagement Guidelines
        </h3>
        <div className="space-y-4" data-id="element-153">
          <p className="flex items-start" data-id="element-154">
            <span className="mr-2" data-id="element-155">🌟</span>
            <span className="text-gray-700" data-id="element-156">
              <strong data-id="element-157">Share Opportunities</strong> Got a lead on a job,
              internship, training, grant, or other opportunity? Don't keep it
              to yourself! Share it with the group—and if you can, recommend
              fellow members for opportunities that match their strengths and
              aspirations.
            </span>
          </p>
          <p className="flex items-start" data-id="element-158">
            <span className="mr-2" data-id="element-159">💡</span>
            <span className="text-gray-700" data-id="element-160">
              <strong data-id="element-161">Support with Ideas</strong> Offer your insights, advice,
              or creative thinking to help others grow their projects,
              businesses, or careers. This is a space where collaboration fuels
              progress.
            </span>
          </p>
          <p className="flex items-start" data-id="element-162">
            <span className="mr-2" data-id="element-163">💬</span>
            <span className="text-gray-700" data-id="element-164">
              <strong data-id="element-165">Engage in Group Conversations</strong> Your voice matters.
              Join discussions, give thoughtful feedback, and help keep our
              conversations rich and inspiring. Active participation helps keep
              our community alive and thriving.
            </span>
          </p>
          <p className="flex items-start" data-id="element-166">
            <span className="mr-2" data-id="element-167">🤝</span>
            <span className="text-gray-700" data-id="element-168">
              <strong data-id="element-169">Attend Networking Salon Events</strong> Whenever possible,
              show up! Our events are great chances to meet new people, share
              ideas, and build meaningful relationships. Your presence adds so
              much to the experience.
            </span>
          </p>
          <p className="flex items-start" data-id="element-170">
            <span className="mr-2" data-id="element-171">📢</span>
            <span className="text-gray-700" data-id="element-172">
              <strong data-id="element-173">Recommend The Networking Salon</strong> Know someone who
              would thrive here? Invite them! Let's grow our circle with
              positive, motivated individuals who can contribute to and benefit
              from the community.
            </span>
          </p>
          <p className="flex items-start" data-id="element-174">
            <span className="mr-2" data-id="element-175">🎁</span>
            <span className="text-gray-700" data-id="element-176">
              <strong data-id="element-177">Share Relevant Products and Events</strong> If you come
              across a service, product, or event that could be useful to others
              here, pass it along! We're all about lifting each other up in ways
              big and small.
            </span>
          </p>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-300" data-id="element-178">
          <h4 className="text-lg font-bold text-gray-800 mb-3" data-id="element-179">
            Why These Guidelines Matter
          </h4>
          <p className="text-gray-700 mb-4" data-id="element-180">
            At The Networking Salon, we're committed to building an inclusive,
            supportive, and opportunity-filled community. These guidelines are
            here to help each of us:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-181">
            <li data-id="element-182">Discover and share valuable opportunities</li>
            <li data-id="element-183">Receive feedback and fresh ideas to grow our work</li>
            <li data-id="element-184">Connect through meaningful, respectful conversations</li>
            <li data-id="element-185">Build relationships through events and ongoing engagement</li>
            <li data-id="element-186">Find and offer resources that can help us all succeed</li>
          </ul>
          <p className="text-gray-700 mt-4" data-id="element-187">
            By following these guidelines, you're helping create a space where
            everyone can grow and thrive.
          </p>
        </div>
      </div>
      {error && <div className="bg-gray-100 border-2 border-gray-400 text-gray-800 p-4 rounded-md mb-6" data-id="element-188">
          {error}
        </div>}
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-189">
        <div className="space-y-4" data-id="element-190">
          <div data-id="element-191">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-1" data-id="element-192">
              Email <span className="text-gray-500" data-id="element-193">(required)</span>
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-194" />
          </div>
          <div data-id="element-195">
            <label htmlFor="fullName" className="block text-gray-700 font-medium mb-1" data-id="element-196">
              Full Name <span className="text-gray-500" data-id="element-197">(required)</span>
            </label>
            <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-198" />
          </div>
          <div data-id="element-199">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-200">
              Sex <span className="text-gray-500" data-id="element-201">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-202">
              {['Male', 'Female'].map(option => <div key={option} className="flex items-center" data-id="element-203">
                  <input type="radio" id={`sex-${option}`} name="sex" value={option} checked={formData.sex === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-204" />
                  <label htmlFor={`sex-${option}`} className="text-gray-700" data-id="element-205">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-206">
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-1" data-id="element-207">
              Phone Number <span className="text-gray-500" data-id="element-208">(required)</span>
            </label>
            <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-209" />
          </div>
          <div data-id="element-210">
            <label htmlFor="location" className="block text-gray-700 font-medium mb-1" data-id="element-211">
              City of Residence{' '}
              <span className="text-gray-500" data-id="element-212">(required)</span>
            </label>
            <select id="location" name="location" value={formData.location} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-213">
              <option value="" data-id="element-214">Select your city</option>
              <option value="Houston Texas" data-id="element-215">Houston Texas</option>
              <option value="Lagos" data-id="element-216">Lagos</option>
              <option value="Abuja" data-id="element-217">Abuja</option>
              <option value="Benin City" data-id="element-218">Benin City</option>
              <option value="Warri" data-id="element-219">Warri</option>
              <option value="Berlin" data-id="element-220">Berlin</option>
              <option value="Port Harcourt" data-id="element-221">Port Harcourt</option>
              <option value="UK" data-id="element-222">UK</option>
              <option value="Other" data-id="element-223">Other</option>
            </select>
          </div>
          <div data-id="element-224">
            <label htmlFor="industry" className="block text-gray-700 font-medium mb-1" data-id="element-225">
              Industry <span className="text-gray-500" data-id="element-226">(required)</span>
            </label>
            <input type="text" id="industry" name="industry" value={formData.industry} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-227" />
          </div>
          <div data-id="element-228">
            <label htmlFor="occupation" className="block text-gray-700 font-medium mb-1" data-id="element-229">
              What do you do? <span className="text-gray-500" data-id="element-230">(required)</span>
            </label>
            <textarea id="occupation" name="occupation" value={formData.occupation} onChange={handleChange} required rows={4} className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-231" />
          </div>
          <div data-id="element-232">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-233">
              Are you committed to actively participating in the ways described
              above? <span className="text-gray-500" data-id="element-234">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-235">
              {['Yes', 'No', 'I have questions'].map(option => <div key={option} className="flex items-center" data-id="element-236">
                  <input type="radio" id={`commitment-${option}`} name="commitment" value={option} checked={formData.commitment === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-237" />
                  <label htmlFor={`commitment-${option}`} className="text-gray-700" data-id="element-238">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-239">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-240">
              Are you okay with your pictures and videos of you taken at our
              events (in-person or online) be used for promotional purposes?{' '}
              <span className="text-gray-500" data-id="element-241">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-242">
              {['Yes', 'No'].map(option => <div key={option} className="flex items-center" data-id="element-243">
                  <input type="radio" id={`mediaConsent-${option}`} name="mediaConsent" value={option} checked={formData.mediaConsent === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-244" />
                  <label htmlFor={`mediaConsent-${option}`} className="text-gray-700" data-id="element-245">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-246">
            <label htmlFor="comments" className="block text-gray-700 font-medium mb-1" data-id="element-247">
              Additional Comments or Suggestions (You can ask your questions
              here): <span className="text-gray-500" data-id="element-248">(optional)</span>
            </label>
            <textarea id="comments" name="comments" value={formData.comments} onChange={handleChange} rows={4} className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-249" />
          </div>
        </div>
        <div className="pt-2" data-id="element-250">
          <Button type="submit" disabled={isSubmitting} className="w-full py-3" data-id="element-251">
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>;
};